// messageCenter/inboxButton.js — the inbox button in the admin menu bar on every backend page (bigplan
// backend/message-center, step 8b; D-35 to D-38): an envelope left of the bug button, linking to the inbox, with the
// unread count over notices and tickets as a small red number (nothing at 0, "99+" from 100). One light request
// (messagecenter_summary) once the page has settled (whenSettled), every 5 minutes while the tab is visible, and right away when the
// tab becomes visible again — never while it is hidden (Q-11) — and when the inbox page announces a read
// (inboxEvents.js). Each answer also carries the notices to pop up now; onSummary hands them on (overlay and window,
// steps 8c/8d). There is no menu entry for the inbox: this button leads there.
//
// Mounted from admin_entry_vite.js into the empty li#inboxButton.navFixed of header_admin.php (only with pageInbox);
// a no-op without it.
import '../../../stylesheet_vite/src/modules/inbox-button.css';
import { str } from '../i18n/str.js';
import { createDisposers } from '../core/disposers.js';
import { createMessageCenterApi } from './messageCenterApi.js';
import { INBOX_CHANGED, announceSummary, countLabel } from './inboxEvents.js';

export const REFRESH_MS = 5 * 60 * 1000;
const INBOX_HREF = '/messagecenter.php';

/**
 * When to ask (D-38): now, then every REFRESH_MS while the document is visible, and at once when it becomes visible
 * again. refresh() is never called while hidden; a timer that falls due while hidden waits for the return.
 */
export function createRefreshSchedule({ refresh, doc = document, env = window, intervalMs = REFRESH_MS }) {
    const disposers = createDisposers();
    let timer = null;

    function plan() {
        env.clearTimeout(timer);
        timer = env.setTimeout(tick, intervalMs);
    }
    function tick() {
        if (doc.hidden) { return; }   // the return to the tab asks instead
        refresh();
        plan();
    }
    function onVisibility() {
        if (!doc.hidden) { tick(); }
    }
    doc.addEventListener('visibilitychange', onVisibility);
    disposers.add(() => doc.removeEventListener('visibilitychange', onVisibility));
    disposers.add(() => env.clearTimeout(timer));

    return {
        start: () => tick(),
        destroy: () => disposers.dispose(),
    };
}

export function mountInboxButton({ doc = document, api = createMessageCenterApi(), env = window, onSummary = () => {} } = {}) {
    const container = doc.getElementById('inboxButton');
    if (!container) { return null; }
    const button = createInboxButton({ container, api, doc, env, onSummary });
    whenSettled(doc, env, () => button.start());
    return button;
}

export const SETTLE_TIMEOUT_MS = 2000;

/**
 * Once the page is built and the browser has a moment (idle, at the latest after SETTLE_TIMEOUT_MS): the summary does
 * not compete with the page's first requests, but it does not wait for the `load` event either — documents.php with
 * its many thumbnails and running requests fires `load` very late (smoke 8e: overlay and window came only after the next
 * click there).
 */
export function whenSettled(doc, env, fn) {
    const go = () => {
        if (typeof env.requestIdleCallback === 'function') { env.requestIdleCallback(() => fn(), { timeout: SETTLE_TIMEOUT_MS }); }
        else { env.setTimeout(fn, 200); }
    };
    if (doc.readyState === 'loading') { doc.addEventListener('DOMContentLoaded', go, { once: true }); } else { go(); }
}

export function createInboxButton({ container, api, doc = document, env = window, onSummary = () => {} }) {
    const disposers = createDisposers();
    let asking = false;   // one request at a time

    const link = doc.createElement('a');
    link.className = 'topo2-btn topo2-btn--solid topo2-btn--noborder topo2-btn--icon topo2-btn--small inboxButton__link';
    link.href = INBOX_HREF;
    link.title = str('INBOX');
    link.setAttribute('aria-label', link.title);
    const icon = doc.createElement('span');
    icon.className = 'topo2-ic topo2-ic--mail';
    const badge = doc.createElement('span');
    badge.className = 'inboxButton__count';
    badge.hidden = true;
    link.append(icon, badge);
    container.replaceChildren(link);

    const schedule = createRefreshSchedule({ refresh: () => refresh(), doc, env });
    disposers.add(() => schedule.destroy());
    // the inbox page read or clicked something away: the counter follows at once
    const onChange = () => refresh();
    doc.addEventListener(INBOX_CHANGED, onChange);
    disposers.add(() => doc.removeEventListener(INBOX_CHANGED, onChange));

    function showCount(unread) {
        const label = countLabel(unread);
        badge.textContent = label;
        badge.hidden = label === '';
        link.title = str('INBOX') + (label ? ' (' + label + ' ' + str('MC_UNREAD') + ')' : '');
        link.setAttribute('aria-label', link.title);
    }

    /** Asks the server; a failure keeps the last count and is logged (the next tick tries again). */
    async function refresh() {
        if (asking) { return; }
        asking = true;
        try {
            const response = await api.summary();
            if (!response?.success) { throw new Error(response?.error ?? 'no answer'); }
            showCount(response.data.unread);
            announceSummary(response.data, doc);   // the inbox page shows the counts on its tabs (step 11a)
            onSummary(response.data);
        } catch (e) {
            console.error('message center: the inbox summary failed', e);
        } finally {
            asking = false;
        }
    }

    function destroy() {
        disposers.dispose();
        container.replaceChildren();
    }

    return { element: link, start: () => schedule.start(), refresh, showCount, destroy };
}
